// ============================================================
// LINES OF SIGHT ON THE GPU MAP (perf overhaul, Stage 2b) - the near plane, the far reach, and a segment against a
// box: shared by world occlusion (who stands behind what) and picking (what is under the pointer), so the two can
// never clip at different planes.
//
// Pure. Zero React, zero three.js.
// ============================================================
import type { Camera, Viewport } from '../../battlefield/projection';
import { eyePosition } from '../../battlefield/projection';
import { viewDirection } from '../../battlefield/scene';
import type { Vec3 } from '../../battlefield/types';

/** projection.ts's near plane: a tenth of the way from the eye to the target (what `clipToEye` cuts at). */
export const NEAR = 0.1;
/** Under the parallel camera the eye is at infinity: a line this long leaves any arena. */
export const FAR_M = 10_000;

export type Segment = { a: Vec3; b: Vec3 };

/** p + d·k. */
export const plus = (p: Vec3, d: Vec3, k: number): Vec3 => ({ x: p.x + d.x * k, y: p.y + d.y * k, z: p.z + d.z * k });

/** p + (q - p)·t. */
export const along = (p: Vec3, q: Vec3, t: number): Vec3 => ({
  x: p.x + (q.x - p.x) * t,
  y: p.y + (q.y - p.y) * t,
  z: p.z + (q.z - p.z) * t,
});

/**
 * The camera's eye for lines of sight: the direction into the screen, and - under perspective - where the eye stands,
 * how deep a point lies along the sight axis from it, and how deep the near plane is. Null `eye`: the parallel camera.
 */
export function lens(camera: Camera, view: Viewport) {
  const into = viewDirection(camera);
  const eye = eyePosition(camera, view);
  if (!eye) return { into, eye: null, depthOf: null, near: 0 };
  const depthOf = (q: Vec3) => (q.x - eye.x) * into.x + (q.y - eye.y) * into.y + (q.z - eye.z) * into.z;
  return { into, eye, depthOf, near: NEAR * depthOf({ x: camera.target.x, y: camera.lift ?? 0, z: camera.target.z }) };
}

/** One axis of the slab test: the span [enter, leave] narrowed to where the segment is inside [lo, hi] on it. */
function slab(span: { enter: number; leave: number }, a: number, d: number, lo: number, hi: number): boolean {
  if (Math.abs(d) < 1e-12) return a >= lo && a <= hi;
  const t1 = (lo - a) / d;
  const t2 = (hi - a) / d;
  span.enter = Math.max(span.enter, Math.min(t1, t2));
  span.leave = Math.min(span.leave, Math.max(t1, t2));
  return span.enter <= span.leave;
}

/** Does the segment pass through the box at all? Exact (the slab test), so a long line across the arena prunes well. */
export function segmentMeetsBox(s: Segment, min: Vec3, max: Vec3): boolean {
  const span = { enter: 0, leave: 1 };
  return (['x', 'y', 'z'] as const).every((k) => slab(span, s.a[k], s.b[k] - s.a[k], min[k], max[k]));
}
